import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router';
import { useState } from 'react';
import { usePSD2OfflineStatusQuery } from 'hooks/webapi';
import * as R from 'Router/routes';
import { Button, HelpInfo, Typography } from 'components';
import { useRiskEvaluationSolvencyQuery } from 'hooks/webapi';
import styles from './OfflinePSD2Error.module.scss';
import { useTranslation } from 'react-i18next';

export default function OfflinePSD2Error() {
  const { t } = useTranslation();
  const params = useParams();

  const [isPending, setPending] = useState(false);
  const { refetch: refetchRiskEvaluationSolvency } =
    useRiskEvaluationSolvencyQuery(false);
  const { data: offlineStatusData } = usePSD2OfflineStatusQuery(
    params.token,
    true,
  );
  const navigate = useNavigate();

  const handleSolvencyCheck = async () => {
    try {
      setPending(true);
      const { data: solvencyRiskEvaluation } =
        await refetchRiskEvaluationSolvency();

      if (solvencyRiskEvaluation?.resolution === 'APPROVED') {
        navigate(R.APPROVED);
        return;
      }

      if (
        solvencyRiskEvaluation?.resolution === 'MANUAL' &&
        ['ASK_FOR_DOCUMENTS', 'Insufficient_PSD2_Income'].includes(
          solvencyRiskEvaluation.message,
        )
      ) {
        navigate(R.INCOME_PROOF);
        return;
      }

      throw new Error('ResulutionError: failed to meet condition');
    } catch (error) {
      // NOTE: For testing and logging purposes

      console.error(error);
    } finally {
      setPending(false);
    }
  };

  const getContentPrefix = () => {
    const statusTransaction = String(offlineStatusData?.transactionHistory);

    switch (statusTransaction) {
      case 'NAME_SURNAME_MISMATCH':
        return 'psd2_error_mismatch';
      case 'INVALID_ACCOUNT_TYPE':
        return 'psd2_error_invalid_account';
      default:
        return 'psd2_error';
    }
  };

  const contentPrefix = getContentPrefix();

  return (
    <>
      <Helmet>
        <title>{t(`${contentPrefix}:html_title`)}</title>
      </Helmet>

      <div className={styles['box-wrapper']}>
        <Typography
          className={styles.title}
          kind="h1"
          size="xxl"
          fontWeight={700}
          data-test="psd2Error-title"
        >
          {t(`${contentPrefix}:page_title`)}
        </Typography>
        <Typography
          className={styles.description}
          data-test="psd2Error-description"
          fontWeight={500}
          size="m2"
        >
          {t(`${contentPrefix}:info_text`)}
        </Typography>
        <Button
          dataTest="psd2-error-continue-button"
          pending={isPending}
          onClick={handleSolvencyCheck}
          className={styles.button}
        >
          {t(`${contentPrefix}:continue_button`)}
        </Button>
        <HelpInfo
          title={t('help_info:title')}
          text={t('help_info:text')}
          dataTest="psd2-error-helpInfo"
        />
      </div>
    </>
  );
}
